import { Close } from "@carbon/icons-react";
import { Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import AsyncButton from "../../../../components/buttons/AsyncButton";
import CancelButton from "../../../../components/buttons/backButton/CancelButton";
import FallbackComponent from "../../../../components/fallbackComponent/FallbackComponent";
import useBookTimeSlotMutation from "../../../../hooks/graphql-hooks/mutations/useBookTimeSlotMutation";
import useNavigateTo from "../../../../hooks/useNavigateTo";
import { TimeSlotFragment } from "../../../../models/autogenerated-deliveryService-gql/graphql";
import { IDeliveryItem } from "../../../../models/deliveryItem";
import { mapToBookTimeSlotInput } from "../../../../utils/mapper";
import TimeSlotSelectionCard from "../../components/timeSlotSelectionCard/TimeSlotSelectionCard";
import "./TimeSlotSelection.scss";

interface TimeSlotSelectionProps {
  timeSlots: TimeSlotFragment[];
  selectedDeliveryItems: IDeliveryItem[];
}

const TimeSlotSelection = (props: TimeSlotSelectionProps) => {
  const { t } = useTranslation();
  const { id: idFromUrl } = useParams();
  const { navigateToDeliveryDetails } = useNavigateTo();
  const { timeSlots, selectedDeliveryItems } = props;

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlotFragment>();

  const [bookTimeSlotMutation, { loading: loadingMutation }] =
    useBookTimeSlotMutation({
      id: idFromUrl || "",
      dataAction: (output) => {
        enqueueSnackbar(t("bookTimeSlotSuccessful"), {
          variant: "success",
        });
        idFromUrl && navigateToDeliveryDetails(idFromUrl);
      },
    });

  const sendBookTimeSlotMutation = () => {
    if (!idFromUrl || !selectedTimeSlot) return;

    const bookTimeSlotInput = mapToBookTimeSlotInput(
      idFromUrl,
      selectedDeliveryItems,
      selectedTimeSlot,
    );

    if (!bookTimeSlotInput) return;

    bookTimeSlotMutation({
      variables: { bookTimeSlotInput },
    }).catch(() => {});
  };

  const memoizedTimeSlotSelectionCards = useMemo(() => {
    return (
      <div className="card-list table">
        {timeSlots.map((timeSlot, index) => {
          return (
            <div key={index} className="card-list-item">
              <TimeSlotSelectionCard
                key={index}
                start={timeSlot.start}
                end={timeSlot.end}
                timeSlotOption={timeSlot}
                selectedTimeSlot={selectedTimeSlot}
                onTimeSlotSelection={(timeSlot) =>
                  setSelectedTimeSlot(timeSlot)
                }
              />
            </div>
          );
        })}
      </div>
    );
  }, [selectedTimeSlot, timeSlots]);

  const renderTimeSlotSelection = () => {
    if (timeSlots.length === 0)
      return (
        <FallbackComponent
          middleIcon={<Close size={32} />}
          text={`${t("noTimeSlotsAvailable")}. ${t("tryAgainLater")}`}
        />
      );
    return (
      <div className="timeslot-selection-container">
        <div className="table-header">
          <Typography className="mb-12" variant="h6">
            {t("selectTimeSlotDescription")}
          </Typography>
        </div>
        {memoizedTimeSlotSelectionCards}
      </div>
    );
  };

  return (
    <div className="page-content">
      {renderTimeSlotSelection()}
      <div className="page-content-button-container">
        <div className="page-content-button-container-button-full-width">
          <AsyncButton
            sendRequest={sendBookTimeSlotMutation}
            loading={loadingMutation}
            disabled={!selectedTimeSlot || loadingMutation}
            variant="contained"
          >
            {t("bookTimeSlot")}
          </AsyncButton>
        </div>
        <div className="page-content-button-container page-content-button-container-button-center">
          <CancelButton />
        </div>
      </div>
    </div>
  );
};
export default TimeSlotSelection;
